import { memo, useEffect, useMemo, useState } from 'react'
import { useSelector, useStore } from 'react-redux'
import { getSecurityFromCache, getSecurityKey, isFnOSecurity } from '../../../util/security'
import { selectIsPrimarySecurityLoading, selectPrimarySecurity } from '../../../selectors/home'
import { ViewType } from '../../../assets/constants'
import { useStyles } from './styles'
import { matchPath, useLocation } from 'react-router-dom'
import ROUTES from '../../../assets/routes'
import { Box } from '@mui/material'
import SubtleShimmer from '../../../components/common/SubtleShimmer'
import LiveMarketText from '../../../components/common/LiveMarketText'
import { RootState } from '../../../store'
import {
    buildLtpViewLiveSelectors,
    hasLtpViewLiveData,
    LtpViewLiveConfig,
    shouldShowLtpViewChange,
} from './ltpViewLiveDisplay'

const LTPView = memo(({ width, view }: { width: number | undefined; view: ViewType }) => {
    const { classes } = useStyles()
    const location = useLocation()
    const store = useStore<RootState>()
    const { key, expressionMode, primary, underlying, expressionSecurities, underlyingSecurityCode } =
        useSelector(selectPrimarySecurity)
    const isPrimarySecurityLoading = useSelector(selectIsPrimarySecurityLoading)

    const isSimulator = useMemo(() => Boolean(matchPath(ROUTES.simulator, location.pathname)), [location.pathname])
    const narrowWidth = !!(width && width <= 800)

    const isDirectPrimaryView =
        view === ViewType.CHART || view === ViewType.SYMBOL || view === ViewType.FINANCIAL_DATA

    const selectedSecurity = useMemo(
        () =>
            expressionMode
                ? null
                : primary && isFnOSecurity(primary) && !isDirectPrimaryView
                  ? underlying
                  : primary,
        [expressionMode, isDirectPrimaryView, primary, underlying]
    )

    const displayKey = useMemo(() => {
        if (expressionMode || !key) return ''
        if (selectedSecurity) return getSecurityKey(selectedSecurity.exchange, selectedSecurity.code)
        const useUnderlyingKey = !primary && !!underlyingSecurityCode && !isDirectPrimaryView
        return useUnderlyingKey ? underlyingSecurityCode : key
    }, [expressionMode, isDirectPrimaryView, selectedSecurity, key, primary, underlyingSecurityCode])

    const securityForStats = useMemo(
        () => selectedSecurity ?? (displayKey ? getSecurityFromCache(displayKey) : undefined),
        [selectedSecurity, displayKey]
    )

    const simulatorUnderlyingKey = useMemo(
        () => getSecurityKey(selectedSecurity?.exchange ?? 0, selectedSecurity?.code ?? ''),
        [selectedSecurity]
    )

    const liveConfig = useMemo((): LtpViewLiveConfig | null => {
        if (!key) return null
        return {
            displayKey,
            expressionKey: key,
            expressionMode,
            isSimulator,
            simulatorUnderlyingKey,
            security: securityForStats,
            expressionSecurities: expressionSecurities ?? [],
            narrowWidth,
        }
    }, [
        key,
        displayKey,
        expressionMode,
        isSimulator,
        simulatorUnderlyingKey,
        securityForStats,
        expressionSecurities,
        narrowWidth,
    ])

    const liveSelectors = useMemo(
        () => (liveConfig ? buildLtpViewLiveSelectors(liveConfig) : null),
        [liveConfig]
    )

    const [dataReady, setDataReady] = useState(() =>
        liveConfig ? hasLtpViewLiveData(store.getState(), liveConfig) : false
    )
    const [showChange, setShowChange] = useState(() =>
        liveConfig ? shouldShowLtpViewChange(store.getState(), liveConfig) : false
    )

    useEffect(() => {
        if (!liveConfig) {
            setDataReady(false)
            setShowChange(false)
            return
        }
        const state = store.getState()
        setDataReady(hasLtpViewLiveData(state, liveConfig))
        setShowChange(shouldShowLtpViewChange(state, liveConfig))
    }, [liveConfig, store])

    useEffect(() => {
        if (!liveConfig || (dataReady && showChange)) return
        return store.subscribe(() => {
            const state = store.getState()
            if (!dataReady && hasLtpViewLiveData(state, liveConfig)) {
                setDataReady(true)
            }
            if (!showChange && shouldShowLtpViewChange(state, liveConfig)) {
                setShowChange(true)
            }
        })
    }, [liveConfig, dataReady, showChange, store])

    const showShimmer =
        !key ||
        (isPrimarySecurityLoading && !securityForStats) ||
        (!!key && !dataReady)

    if (showShimmer || !liveSelectors) {
        return (
            <div className={classes.container}>
                <div className="security-ltp" style={{ display: 'flex', alignItems: 'baseline', gap: 4, marginLeft: 2 }}>
                    <SubtleShimmer loading>
                        <Box sx={{ height: 20, width: 72, borderRadius: 0.5 }} />
                    </SubtleShimmer>
                    <SubtleShimmer loading>
                        <Box sx={{ height: 12, width: 24, borderRadius: 0.5 }} />
                    </SubtleShimmer>
                    <SubtleShimmer loading>
                        <Box sx={{ height: 16, width: width && width <= 800 ? 48 : 100, borderRadius: 0.5 }} />
                    </SubtleShimmer>
                </div>
            </div>
        )
    }

    const changeStyle = {
        fontSize: '14px',
        textAlign: 'right' as const,
        marginLeft: '8px',
    }

    return (
        <div className={classes.container}>
            <div className="security-ltp font-feature-num">
                <LiveMarketText
                    selectText={liveSelectors.ltp}
                    style={{ fontSize: '1rem', marginLeft: '4px' }}
                />
                <span className="inr-tag" style={{ marginLeft: '4px', fontSize: '10px' }}>
                    INR
                </span>
                {showChange && (
                    <LiveMarketText
                        selectText={liveSelectors.change}
                        selectColor={liveSelectors.changeColor}
                        style={changeStyle}
                    />
                )}
            </div>
        </div>
    )
})
LTPView.displayName = 'LtpView'
export default LTPView
